import { useContext } from 'react';
import { useBrowser } from '../../services/BrowserService';
import { UnknownRecord } from '../../types/UnknownRecord';
import { useEventBus } from '../../util/EventBus';
import { AnalyticsContext } from '../AnalyticsContext';
import { TrackingData } from '../TrackingData';
import { TrackingEvent } from '../TrackingEvent';

/** Observes analytics events and dispatches them to Adobe Analytics when needed.
 *
 * @requires <AnalyticsProvider> as an ancestor in the tree. */
export const AdobeAnalyticsDispatcher = () => {
  const { window } = useBrowser();
  const { bus } = useContext(AnalyticsContext);

  useEventBus(bus, ({ name, data }) => {
    getDataLayer(window).push({ event: name, ...translateTrackingDataForAdobe(data) });
  });

  return null;
};

const translateTrackingDataForAdobe = (data: TrackingData): object => {
  const translated = translateObjectForAdobe(data);

  /** To allow merging data from multiple flagr flags on our internal analytics model, we use a
   * key-value map. However our Adobe model wants to see an array of {key, variant} objects.
   */
  if (data.flagr) {
    translated.flagr = Object.entries(data.flagr).map(([key, variant]) => [{ key, variant }]);
  }

  return translated;
};

/** Our Adobe Analytics setup cannot distinguish between falsy values and missing values.
 * As a result, falsy numbers and booleans must be sent as strings.
 */
const translateValueForAdobe = (value: unknown) => {
  if (value === false) return 'false';
  if (value === 0) return 'zero';
  if (typeof value === 'object' && value !== null) return translateObjectForAdobe(value);
  return value;
};

const translateObjectForAdobe = (data: object): UnknownRecord => {
  const translated: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    translated[key] = translateValueForAdobe(value);
  }

  return translated;
};

declare global {
  interface Window {
    /** The place where Adobe will look for an existing event array, and will inject its own "live" array */
    launchEvents?: unknown[];
    /** Visitor ID Service */
    Visitor: Visitor;
  }
  class Visitor {
    /** getInstance returns a visitor ID object for the specified Experience Cloud organization ID. This is required to initialize the visitor ID object provided to AppMeasurement through s.visitor.
     * Docs: https://experienceleague.adobe.com/docs/id-service/using/id-service-api/methods/getinstance.html?lang=en  */
    getInstance: (id: string) => VisitorInstance;
  }

  class VisitorInstance {
    /** This function lets you share a visitor’s Experience Cloud ID across domains when browsers block third-party cookies. To use this function, you must have implemented the ID service and own the source and destination domains. Available in VisitorAPI.js version 1.7.0 or higher.
     * Docs: https://experienceleague.adobe.com/docs/id-service/using/id-service-api/methods/appendvisitorid.html?lang=en */
    appendVisitorIDsTo: (destinationUrl: string) => string;
  }
}

/** Returns the global data layer, creating it if it does not exist. */
export const getDataLayer = (window: Window | undefined) => {
  if (window) {
    if (!window.launchEvents) window.launchEvents = [];
    return window.launchEvents;
  } else {
    return [];
  }
};

export type AdobeEventRecord = TrackingData & {
  event: TrackingEvent;
};
